/**
 * @module ldd-fetch-interceptor
 * @description Общий перехватчик HTTP-ответов Supabase для LDD-логирования.
 * Используется в client.ts, server.ts и admin.ts для устранения дублирования.
 */

// START_CONTRACT: LDD_FETCH_INTERCEPTOR
// PURPOSE: Единая точка перехвата ошибок Supabase → LDD лог
// SCOPE: Все Supabase-клиенты (Browser, Server, Admin)
// INPUT: Logger с методом error()
// OUTPUT: fetch-функция с перехватом ошибок
// END_CONTRACT

export interface LddLogger {
  error: (message: string, meta?: any) => void;
}

/**
 * Создаёт обёртку над fetch, которая перехватывает HTTP-ошибки Supabase
 * и логирует их в LDD в структурированном JSON-формате.
 *
 * @param logger — объект с методом error (clientLogger или serverLogger)
 * @param label — метка источника: "Client", "Server", "Admin"
 */
export function createLddFetchInterceptor(
  logger: LddLogger,
  label: string = "Supabase"
) {
  return async (
    url: RequestInfo | URL,
    options?: RequestInit
  ): Promise<Response> => {
    const response = await fetch(url, options);

    if (!response.ok) {
      try {
        const resClone = response.clone();
        const errorText = await resClone.text();
        let parsedError: any = errorText;

        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          try {
            parsedError = JSON.parse(errorText);
          } catch {
            // Fallback — оставляем текст, если JSON битый
          }
        }

        // Извлекаем чистый путь без хоста
        const urlString =
          typeof url === "string"
            ? url
            : url instanceof URL
              ? url.toString()
              : (url as Request).url;
        const urlObj = new URL(urlString);
        const path = urlObj.pathname + urlObj.search;

        logger.error(`${label} Error [${response.status}]`, {
          url: path,
          method: options?.method || "GET",
          status: response.status,
          headers: Object.fromEntries(response.headers.entries()),
          error: parsedError,
        });
      } catch {
        // Игнорируем ошибки клонирования
      }
    }

    return response;
  };
}
